import { Elm } from './Main.elm';
import 'configurable-date-input-polyfill';
import Papa from 'papaparse';
import Dexie from 'dexie';

console.log("Starting app...");

const db = new Dexie('readcode-db');
const VERSION = 2;
const CONTENT_VERSION = '2025-10-18-rc-snomed-refresh'; // change whenever data files change

let app = Elm.Main.init({
  node: document.getElementById('root')
});

db.version(VERSION).stores({
  meta: '&key',
  readcode: '++id, Readcode, Description',
  snomed: '++id, ConceptSCTID, PreferredTerm, ReadCode, ReadTerm'
}).upgrade(async (tx) => {
  // Optional: if you want a clean slate on first run of v2
  await tx.table('readcode').clear();
  await tx.table('snomed').clear();
  await tx.table('meta').put({ key: 'contentVersion', value: 'initial' });
});

function parseCsv(url) {
  return new Promise((resolve, reject) => {
    Papa.parse(url + `?v=${encodeURIComponent(CONTENT_VERSION)}`, {
      header: true,
      skipEmptyLines: true,
      download: true,
      complete: (results) => {
        const rows = results.data;
        console.log(`Parsed ${rows.length} rows from ${url}`);
        resolve(rows);
      },
      error: reject
    });
  });
}

async function ensureContentUpToDate() {
  await db.open();

  const current = (await db.table('meta').get('contentVersion'))?.value;

  if (current === CONTENT_VERSION) {
    console.log('Content up-to-date.');
    app.ports.receive_db_ready.send(true);
    return;
  }

  console.log('Updating content…');

  // Load CSVs and upsert (bulkPut) atomically
  const [readcodeRows, snomedRows] = await Promise.all([
    parseCsv('/readcode.csv'),
    parseCsv('/snomed.csv')
  ]);

  await db.transaction('rw', db.readcode, db.snomed, db.meta, async () => {
    // Upsert based on stable keys
    await db.readcode.clear(); // if you prefer full replace
    await db.snomed.clear();   // (or skip clears to truly upsert)
    console.log("Cleared existing data");

    // bulkPut is idempotent if keys are stable
    await db.readcode.bulkPut(readcodeRows);
    await db.snomed.bulkPut(snomedRows);
    console.log("Inserted new data");

    await db.meta.put({ key: 'contentVersion', value: CONTENT_VERSION });
    console.log("Updated content version metadata");
  }).then(() => {
    console.log("Transaction committed");
    app.ports.receive_db_ready.send(true);
  }).catch(err => {
    console.error(err.stack);
  });
}

ensureContentUpToDate().catch((e) => {
  console.error('Failed to update content', e);
  app.ports.receive_db_ready.send(true); // fail-open so the app still starts
})

async function initReadcodeDb() {
  await ensureContentUpToDate();
}

app.ports.send_readcode_query.subscribe(function (query) {
  db.readcode.filter((readcode) => {
    // filter by Readcode or Description, case insensitive
    // return true if each space-separated word in query is found in Description
    query = query.toLowerCase();
    let queryWords = query.split(" ");
    let description = readcode.Description.toLowerCase();
    return queryWords.every(word => description.includes(word));
  }).toArray().then(function (results) {
    app.ports.receive_readcode.send(results);
  });
})

app.ports.send_snomed_query.subscribe(function (query) {
  db.snomed.filter((snomed) => {
    // filter by preferred term or read term, case insensitive
    // strip whitespace and convert to lowercase
    // return true if each space-separated word in query is found in either preferred term or read term

    query = query.toLowerCase();
    let queryWords = query.split(" ");
    let preferredTerm = snomed.PreferredTerm.toLowerCase();
    let readTerm = snomed.ReadTerm.toLowerCase();
    return queryWords.every(word => preferredTerm.includes(word) || readTerm.includes(word));
  })
    .toArray()
    .then(function (results) {
      app.ports.receive_snomed.send(results);
    });
})

app.ports.init_tooltips.subscribe(function () {
  $('[data-toggle="tooltip"]').tooltip()
  console.log("Tooltips initialized");
})
